<template>
  <div class="video-page-container">
    <!-- 顶部导航栏 -->
    <div class="top-bar">
      <div class="menu-btn" @click.stop="toggleDrawer">
        <svg viewBox="0 0 24 24" width="24" height="24" stroke="currentColor" stroke-width="2" fill="none" stroke-linecap="round" stroke-linejoin="round"><line x1="3" y1="12" x2="21" y2="12"></line><line x1="3" y1="6" x2="21" y2="6"></line><line x1="3" y1="18" x2="21" y2="18"></line></svg>
      </div>

      <div class="category-scroll-wrapper" ref="categoryWrapper">
        <div 
          class="category-item" 
          :class="{ active: currentCategory === cat.value }"
          v-for="cat in categoryList" 
          :key="cat.value"
          @click.stop="switchCategory(cat.value)"
        >
          {{ cat.label }}
        </div>
      </div>
      
      <div class="mute-btn" @click.stop="toggleGlobalMute">
        <svg v-if="isMuted" viewBox="0 0 24 24" width="24" height="24" stroke="currentColor" stroke-width="2" fill="none" stroke-linecap="round" stroke-linejoin="round"><path d="M11 5L6 9H2v6h4l5 4V5z"></path><line x1="23" y1="9" x2="17" y2="15"></line><line x1="17" y1="9" x2="23" y2="15"></line></svg>
        <svg v-else viewBox="0 0 24 24" width="24" height="24" stroke="currentColor" stroke-width="2" fill="none" stroke-linecap="round" stroke-linejoin="round"><path d="M11 5L6 9H2v6h4l5 4V5z"></path><path d="M19.07 4.93a10 10 0 0 1 0 14.14M15.54 8.46a5 5 0 0 1 0 7.07"></path></svg>
      </div>
    </div>

    <!-- 视频滚动容器 -->
    <div 
      class="video-scroll-container" 
      ref="scrollContainer"
      @scroll="handleScroll"
    >
      <!-- 关键修复：使用 product.id 作为 key，确保 DOM 唯一性 -->
      <div 
        class="video-item" 
        v-for="(product, index) in productList" 
        :key="product.id"
      >
        <!-- 视频层 -->
        <!-- 关键修复：使用函数式 ref 绑定，确保获取到正确的 DOM 元素 -->
        <video
          :ref="(el) => bindVideoRef(el, product.id)"
          class="bg-video"
          :src="getProxyVideoUrl(product.videoUrl)"
          loop
          playsinline
          webkit-playsinline
          x5-video-player-type="h5"
          x5-video-player-fullscreen="true"
          :preload="getPreload(index)"
          :muted="isMuted"
          crossorigin="anonymous"
          @timeupdate="(e) => onTimeUpdate(e, product.id)"
          @loadedmetadata="(e) => onLoadedMetadata(e, product.id)"
          @error="() => handleVideoError(product.id)"
          @waiting="() => handleVideoWaiting(product.id)"
          @playing="() => handleVideoPlaying(product.id)"
          @pause="() => handleVideoPause(product.id)"
        ></video>

        <!-- 封面图覆盖层 -->
        <!-- 关键修复：判断条件改为 playingId !== product.id，彻底解决封面闪烁 -->
        <div
          v-if="playingId !== product.id && product.coverImage && !isScrolling"
          class="cover-overlay"
          @click.stop="handleVideoClick(product.id)"
        >
          <img :src="product.coverImage" alt="cover" class="cover-image" />
          <div class="play-icon-overlay">
            <svg viewBox="0 0 24 24" width="60" height="60" fill="rgba(255,255,255,0.8)"><path d="M8 5v14l11-7z"/></svg>
          </div>
        </div>

        <!-- 点击区域 -->
         <div
          v-if="playingId === product.id"
          class="click-layer"
          @click.stop="handleVideoClick(product.id)"
        ></div>

        <!-- 视频加载中/缓冲提示层 -->
        <div v-if="playingId === product.id && (videoStatus[product.id] === 'loading' || videoStatus[product.id] === 'buffering')" class="video-status-overlay">
          <div class="loading-spinner"></div>
          <span class="status-text">{{ videoStatus[product.id] === 'loading' ? '加载中...' : '缓冲中...' }}</span>
        </div>

        <!-- 视频加载失败提示层 -->
        <div v-if="videoStatus[product.id] === 'error'" class="video-error-overlay">
          <div class="error-content" @click.stop="retryVideo(product.id)">
            <svg class="error-icon" viewBox="0 0 24 24" width="48" height="48" stroke="currentColor" stroke-width="2" fill="none" stroke-linecap="round" stroke-linejoin="round">
              <circle cx="12" cy="12" r="10"></circle>
              <line x1="12" y1="8" x2="12" y2="12"></line>
              <line x1="12" y1="16" x2="12.01" y2="16"></line>
            </svg>
            <p>视频暂无法播放</p>
            <span class="retry-hint">点击重试 · 或上下滑动切换</span>
          </div>
        </div>

        <!-- 自动播放被拦截时的手动播放按钮（移动端兜底） -->
        <div
          v-if="needManualPlay[product.id] && videoStatus[product.id] !== 'error'"
          class="manual-play-overlay"
          @click.stop="manualPlay(product.id)"
        >
          <div class="manual-play-btn">
            <svg viewBox="0 0 24 24" width="40" height="40" fill="#fff"><path d="M8 5v14l11-7z"/></svg>
          </div>
          <span class="manual-play-text">点击播放</span>
        </div>

        <div class="overlay"></div>

        <div class="info-area">
          <h3 class="product-name">@{{ product.name }}</h3>
          <div class="info-row">
            <span class="label">规格:</span> {{ product.specification }}
          </div>
          <!-- <div class="info-row">
            <span class="label">单价:</span> ¥{{ product.unitPrice }} / {{ product.unit }}
          </div> -->
          <div class="info-row">
            <span class="label">印刷:</span> {{ product.packagePrintTech }}
          </div>
          <div class="info-row">
            <span class="label">外包装:</span> {{ product.outerPackageSize }} cm
          </div>
        </div>

        <div class="right-actions">
          <div 
            class="action-btn wishlist-btn" 
            :class="{ 'animate-like': isInWishlist(product.id) && justAddedId === product.id }"
            @click.stop="handleWishlistClick(product, $event)"
          >
            <svg viewBox="0 0 24 24" width="24" height="24" :fill="isInWishlist(product.id) ? '#ff4d4f' : 'none'" :stroke="isInWishlist(product.id) ? '#ff4d4f' : '#fff'" stroke-width="2">
              <path d="M20.84 4.61a5.5 5.5 0 0 0-7.78 0L12 5.67l-1.06-1.06a5.5 5.5 0 0 0-7.78 7.78l1.06 1.06L12 21.23l7.78-7.78 1.06-1.06a5.5 5.5 0 0 0 0-7.78z"></path>
            </svg>
          </div>

          <div class="speed-ball" @click.stop="toggleSpeed(product.id)">
            {{ speedMap[product.id] === 2.0 ? '2.0x' : '1.0x' }}
          </div>
        </div>

        <div class="video-counter">
          <!-- 显示当前索引 + 1，基于当前列表长度 -->
          {{ String(index + 1).padStart(2, '0') }} / {{ productList.length }}
        </div>

        <div class="progress-control-area">
          <div class="progress-bar-container" @click.stop="seekVideo(product.id, $event)">
            <div class="progress-bg"></div>
            <div class="progress-current" :style="{ width: getProgressPercent(product.id) + '%' }"></div>
            <div class="progress-thumb" :style="{ left: getProgressPercent(product.id) + '%' }"></div>
          </div>
          <div class="time-display">
            <span>{{ formatTime(currentTimes[product.id] || 0) }}</span>
            <span>/</span>
            <span>{{ formatTime(durations[product.id] || 0) }}</span>
          </div>
        </div>
        
        <div v-if="index === productList.length - 1 && noMore" class="no-more-tip">
          没有下一个了
        </div>
      </div>
      
      <div class="bottom-spacer"></div>
    </div>
    
    <div v-if="loading && productList.length === 0" class="global-loading-mask">
      <div class="loading-spinner-large"></div>
      <p>正在获取产品...</p>
    </div>

    <!-- 左侧抽屉 (意向产品) -->
    <transition name="drawer-slide">
      <div v-if="isDrawerOpen" class="drawer-mask" @click.stop="closeDrawer"></div>
    </transition>
    <transition name="drawer-slide">
      <div v-if="isDrawerOpen" class="drawer-container">
        <div class="drawer-header">
          <h3>我的意向产品</h3>
          <span class="close-drawer" @click="closeDrawer">×</span>
        </div>
        
        <div class="drawer-content">
          <div v-if="wishlist.length === 0" class="empty-wishlist">
            暂无意向产品
          </div>
          <div 
            v-for="item in wishlist" 
            :key="item.id" 
            class="wishlist-item"
            @click="jumpToProduct(item.id)"
          >
            <div class="item-info">
              <div class="item-name">{{ item.name }}</div>
              <div class="item-spec">{{ item.specification }}</div>
            </div>
            <div class="item-delete" @click.stop="removeFromWishlist(item.id)">
              <svg viewBox="0 0 24 24" width="20" height="20" stroke="currentColor" stroke-width="2" fill="none"><polyline points="3 6 5 6 21 6"></polyline><path d="M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6m3 0V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2"></path></svg>
            </div>
          </div>
        </div>

        <div class="drawer-footer">
          <div class="input-group">
            <input 
              type="tel" 
              v-model="intentPhone" 
              placeholder="请输入手机号码" 
              class="phone-input"
              maxlength="11"
            />
            <div class="action-buttons">
              <button 
                class="submit-btn" 
                @click="submitIntentOrder" 
                :disabled="!isValidPhone || isSubmitting || wishlist.length === 0"
              >
                {{ isSubmitting ? '提交中...' : '提交' }}
              </button>
              <button 
                class="clear-btn" 
                @click="clearWishlist"
                :disabled="isSubmitting || wishlist.length === 0"
              >
                清空
              </button>
            </div>
          </div>
        </div>
      </div>
    </transition>

    <transition name="toast-fade">
      <div v-if="toast.visible" class="toast-container" :class="toast.type">
        {{ toast.message }}
      </div>
    </transition>
  </div>
</template>

<script setup>
import { ref, onMounted, onUnmounted, nextTick, reactive, computed } from 'vue';
import { BASE_URL } from '@/config'; 

// --- 状态定义 ---
const categoryList = ref([{ label: '全部', value: '' }]);
const productList = ref([]);
const currentCategory = ref('');
const loading = ref(false);
const pageIndex = ref(1);
const pageSize = ref(1000);
const totalPage = ref(1);
const totalNum = ref(0);
const noMore = ref(false);
const scrollContainer = ref(null);

// 当前可视区索引，用于预加载策略
const activeIndex = ref(0);

// 预加载策略：仅当前及下一个视频请求数据，其余设为 none。
// 关键：列表全量渲染时，若每个 video 都 preload=metadata，会同时向同一域名发起
// 大量请求，撑爆浏览器并发连接数上限（通常 6 个），导致首个视频被排队阻塞甚至失败。
const getPreload = (index) => {
  if (index === activeIndex.value || index === activeIndex.value + 1) {
    return 'auto';
  }
  return 'none';
};

// 关键修复：使用 Map/Object 存储视频引用，Key 为 product.id，彻底解耦索引
const videoRefsMap = reactive({});

const isMuted = ref(true);
const videoStatus = reactive({});
const speedMap = reactive({});
const currentTimes = reactive({});
const durations = reactive({});
// 自动播放被浏览器拦截的视频 id，需展示中央播放按钮等待用户点击
const needManualPlay = reactive({});

const isDrawerOpen = ref(false);
const wishlist = ref([]); 
// 关键修复：playingId 存储当前正在播放的产品 ID，而不是索引
const playingId = ref(null); 
const isScrolling = ref(false); 

const intentPhone = ref('');
const isSubmitting = ref(false);
const justAddedId = ref(null);

const toast = reactive({
  visible: false,
  message: '',
  type: 'success',
  timer: null
});

// 绑定视频引用
const bindVideoRef = (el, productId) => {
  if (el) {
    videoRefsMap[productId] = el;
  } else {
    delete videoRefsMap[productId];
  }
};

const initWishlist = () => {
  const stored = localStorage.getItem('jp_wishlist');
  if (stored) {
    try {
      wishlist.value = JSON.parse(stored);
    } catch (e) {
      wishlist.value = [];
    }
  }
  const savedPhone = localStorage.getItem('jp_intent_phone');
  if (savedPhone) {
    intentPhone.value = savedPhone;
  }
};

const saveWishlist = () => {
  localStorage.setItem('jp_wishlist', JSON.stringify(wishlist.value));
};

const getProxyVideoUrl = (originalUrl) => {
  if (!originalUrl) return '';
  return `${BASE_URL}/ClientApi/ProxyVideo?url=${encodeURIComponent(originalUrl)}`;
};

const formatTime = (seconds) => {
  if (!seconds || isNaN(seconds)) return "00:00";
  const m = Math.floor(seconds / 60);
  const s = Math.floor(seconds % 60);
  return `${String(m).padStart(2, '0')}:${String(s).padStart(2, '0')}`;
};

const getProgressPercent = (productId) => {
  const duration = durations[productId];
  const current = currentTimes[productId];
  if (!duration || duration === 0) return 0;
  return (current / duration) * 100;
};

const isInWishlist = (productId) => {
  return wishlist.value.some(item => item.id === productId);
};

const isValidPhone = computed(() => /^1[3-9]\d{9}$/.test(intentPhone.value));

const showToast = (message, type = 'success', duration = 2500) => {
  if (toast.timer) clearTimeout(toast.timer);
  toast.message = message;
  toast.type = type;
  toast.visible = true;
  toast.timer = setTimeout(() => { toast.visible = false; }, duration);
};

// --- API 请求 ---
const fetchProducts = async (isRefresh = false) => {
  if (loading.value) return;
  loading.value = true;

  try {
    const url = `${BASE_URL}/ClientApi/GetProducts`;
    const params = new URLSearchParams();
    params.append('pageIndex', isRefresh ? 1 : pageIndex.value);
    params.append('pageSize', pageSize.value);
    if (currentCategory.value) {
      params.append('Category', currentCategory.value);
    }

    const response = await fetch(`${url}?${params.toString()}`);
    const res = await response.json();

    if (res.code === 200) {
      const data = res.data;
      
      if (isRefresh && data.categories && data.categories.length > 0) {
        const extraCats = data.categories.map(c => ({ label: c, value: c }));
        const existingValues = new Set(categoryList.value.map(c => c.value));
        extraCats.forEach(c => {
          if (!existingValues.has(c.value)) categoryList.value.push(c);
        });
      }

      const newProducts = data.products.result || [];
      totalNum.value = data.products.totalNum || 0;
      totalPage.value = data.products.totalPage || 1;
      
      if (isRefresh) {
        productList.value = newProducts;
        pageIndex.value = 1;
        
        // 清空所有状态
        Object.keys(currentTimes).forEach(k => delete currentTimes[k]);
        Object.keys(durations).forEach(k => delete durations[k]);
        Object.keys(videoRefsMap).forEach(k => delete videoRefsMap[k]);
        Object.keys(needManualPlay).forEach(k => delete needManualPlay[k]);
        playingId.value = null;
        activeIndex.value = 0;
        
        await nextTick();
        if (scrollContainer.value) scrollContainer.value.scrollTop = 0;
        
        // 自动播放第一个
        if (productList.value.length > 0) {
          setTimeout(() => playVideoById(productList.value[0].id), 100);
        }
      } else {
        // 关键修复：去重合并，防止重复数据导致列表虚增
        const existingIds = new Set(productList.value.map(p => p.id));
        const uniqueNewProducts = newProducts.filter(p => !existingIds.has(p.id));
        
        if (uniqueNewProducts.length > 0) {
          productList.value = [...productList.value, ...uniqueNewProducts];
        } else {
          // 如果没有新数据，标记为没有更多
          noMore.value = true;
        }
      }

      noMore.value = pageIndex.value >= totalPage.value;

      newProducts.forEach(p => {
        if (!speedMap[p.id]) speedMap[p.id] = 1.0;
        if (!videoStatus[p.id]) videoStatus[p.id] = 'loading';
      });

    } else {
      console.error('API Error:', res.msg);
    }
  } catch (error) {
    console.error('Request Failed:', error);
  } finally {
    loading.value = false;
  }
};

const switchCategory = (catValue) => {
  if (currentCategory.value === catValue) return;
  currentCategory.value = catValue;
  pageIndex.value = 1;
  noMore.value = false;
  fetchProducts(true); 
};

const toggleGlobalMute = () => {
  isMuted.value = !isMuted.value;
  Object.values(videoRefsMap).forEach(v => {
    if (v) v.muted = isMuted.value;
  });
};

const toggleSpeed = (productId) => {
  const videoEl = videoRefsMap[productId];
  if (videoEl) {
    const currentSpeed = speedMap[productId] || 1.0;
    const newSpeed = currentSpeed === 1.0 ? 2.0 : 1.0;
    try {
      videoEl.playbackRate = newSpeed;
      speedMap[productId] = newSpeed;
    } catch (e) { console.warn(e); }
  }
};

let clickTimer = null;
const handleVideoClick = (productId) => {
  if (clickTimer) {
    clearTimeout(clickTimer);
    clickTimer = null;
    handleWishlistClick(productList.value.find(p => p.id === productId));
  } else {
    clickTimer = setTimeout(() => {
      const videoEl = videoRefsMap[productId];
      if (videoEl) {
        if (videoEl.paused) videoEl.play().catch(e => console.warn(e));
        else videoEl.pause();
      }
      clickTimer = null;
    }, 300);
  }
};

// 用户主动点击中央按钮播放（真实用户手势，自动播放策略必定放行）
const manualPlay = (productId) => {
  const videoEl = videoRefsMap[productId];
  if (!videoEl) return;
  videoEl.muted = isMuted.value;
  videoEl.play().then(() => {
    delete needManualPlay[productId];
    playingId.value = productId;
  }).catch(e => console.warn('Manual play failed:', e));
};

// 视频加载失败后点击重试：重置状态并强制重新加载
const retryVideo = (productId) => {
  const videoEl = videoRefsMap[productId];
  if (!videoEl) return;
  videoStatus[productId] = 'loading';
  try {
    videoEl.load();
  } catch (e) { /* ignore */ }
  videoEl.muted = isMuted.value;
  videoEl.play().then(() => {
    playingId.value = productId;
  }).catch(error => {
    if (error.name === 'NotAllowedError') {
      needManualPlay[productId] = true;
      videoStatus[productId] = 'paused';
    }
  });
};

const handleVideoPause = (productId) => {
  if (playingId.value === productId) {
    playingId.value = null; 
  }
};

const handleVideoPlaying = (productId) => {
  playingId.value = productId;
  videoStatus[productId] = 'playing';
};

const handleWishlistClick = (product) => {
  if (!product) return;
  toggleWishlist(product);
  if (isInWishlist(product.id)) {
    justAddedId.value = product.id;
    setTimeout(() => { justAddedId.value = null; }, 400);
  }
};

const toggleWishlist = (product) => {
  const index = wishlist.value.findIndex(item => item.id === product.id);
  if (index === -1) {
    wishlist.value.push({
      id: product.id,
      name: product.name,
      specification: product.specification,
      coverImage: product.coverImage,
      videoUrl: product.videoUrl
    });
  } else {
    wishlist.value.splice(index, 1);
  }
  saveWishlist();
};

const removeFromWishlist = (productId) => {
  const index = wishlist.value.findIndex(item => item.id === productId);
  if (index !== -1) {
    wishlist.value.splice(index, 1);
    saveWishlist();
  }
};

const clearWishlist = () => {
  if (wishlist.value.length === 0) return;
  wishlist.value = [];
  saveWishlist();
  showToast('意向产品已清空', 'success', 1500);
};

const submitIntentOrder = async () => {
  if (!isValidPhone.value) {
    showToast('请输入有效的手机号码', 'error');
    return;
  }
  if (wishlist.value.length === 0) {
    showToast('请先添加意向产品', 'error');
    return;
  }
  isSubmitting.value = true;
  try {
    const items = wishlist.value.map(item => ({
      productId: item.id,
      productName: item.name,
      specification: item.specification
    }));
    const payload = { phone: intentPhone.value, items };
    const response = await fetch(`${BASE_URL}/ClientApi/CreateIntentOrder`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(payload)
    });
    const res = await response.json();
    if (res.code === 200) {
      localStorage.setItem('jp_intent_phone', intentPhone.value);
      showToast('提交成功！专属VIP业务将尽快联系您。', 'success');
      closeDrawer();
    } else {
      showToast('提交失败: ' + (res.msg || '未知错误'), 'error');
    }
  } catch (error) {
    showToast('网络请求失败，请稍后重试', 'error');
  } finally {
    isSubmitting.value = false;
  }
};

const toggleDrawer = () => { isDrawerOpen.value = !isDrawerOpen.value; };
const closeDrawer = () => { isDrawerOpen.value = false; };

const jumpToProduct = (productId) => {
  const index = productList.value.findIndex(p => p.id === productId);
  if (index !== -1) {
    closeDrawer();
    // 立即将渲染窗口移到目标，确保滚动到位时 video 已挂载
    activeIndex.value = index;
    const container = scrollContainer.value;
    const targetScroll = index * container.clientHeight;
    container.scrollTo({ top: targetScroll, behavior: 'smooth' });
    setTimeout(() => {
      playVideoById(productId);
    }, 500);
  } else {
    showToast("该产品不在当前加载列表中", "error");
  }
};

const onTimeUpdate = (e, productId) => {
  currentTimes[productId] = e.target.currentTime;
};

const onLoadedMetadata = (e, productId) => {
  durations[productId] = e.target.duration;
  if (videoStatus[productId] === 'loading') {
    videoStatus[productId] = 'paused';
  }
};

const handleVideoError = (productId) => { videoStatus[productId] = 'error'; };
const handleVideoWaiting = (productId) => {
  if (videoStatus[productId] !== 'error') videoStatus[productId] = 'buffering';
};

const seekVideo = (productId, event) => {
  const videoEl = videoRefsMap[productId];
  if (videoEl && durations[productId] && event) {
    const rect = event.currentTarget.getBoundingClientRect();
    const pos = (event.clientX - rect.left) / rect.width;
    // 确保 pos 在 0-1 之间
    const safePos = Math.max(0, Math.min(1, pos));
    const seekTime = safePos * durations[productId];
    try {
      videoEl.currentTime = seekTime;
    } catch (err) {
      console.warn("Seek failed", err);
    }
  }
};

// 滚动监听
let scrollTimer = null;
const handleScroll = () => {
  if (!scrollContainer.value) return;
  isScrolling.value = true;

  // 实时更新渲染窗口（轻量，仅算索引），确保滑动时下一个 video 已挂载
  const container = scrollContainer.value;
  const idx = Math.round(container.scrollTop / container.clientHeight);
  if (idx >= 0 && idx < productList.value.length && idx !== activeIndex.value) {
    activeIndex.value = idx;
  }

  if (scrollTimer) clearTimeout(scrollTimer);

  scrollTimer = setTimeout(() => {
    scrollTimer = null;
    isScrolling.value = false;

    const scrollTop = container.scrollTop;
    const clientHeight = container.clientHeight;

    // 计算当前可视区域的索引
    const currentIndex = Math.round(scrollTop / clientHeight);

    // 边界检查
    if (currentIndex < 0 || currentIndex >= productList.value.length) return;

    const currentProduct = productList.value[currentIndex];
    if (currentProduct) {
      playVideoById(currentProduct.id);
    }

    // 加载更多
    if (currentIndex >= productList.value.length - 2 && !noMore.value && !loading.value) {
       if (scrollTop + clientHeight >= container.scrollHeight - 100) {
         pageIndex.value++;
         fetchProducts();
       }
    }
  }, 50);
};

// 核心播放逻辑：基于 ID
const playVideoById = (targetId, retry = 0) => {
  // 如果已经在播放这个 ID，且未暂停，则跳过
  if (playingId.value === targetId) {
    const v = videoRefsMap[targetId];
    if (v && !v.paused) return;
  }

  // 1. 暂停上一个
  if (playingId.value && playingId.value !== targetId) {
    const prevV = videoRefsMap[playingId.value];
    if (prevV && !prevV.paused) {
      try { 
        prevV.pause(); 
      } catch(e) {
        // Ignore pause error
      }
    }
    if (videoStatus[playingId.value] === 'loading' || videoStatus[playingId.value] === 'buffering') {
       videoStatus[playingId.value] = 'paused';
    }
  }

  // 2. 播放新的
  const currentV = videoRefsMap[targetId];
  if (currentV) {
    if (videoStatus[targetId] === 'error') return;

    playingId.value = targetId;

    // 同步渲染窗口到目标位置（覆盖跳转、刷新等非滚动触发路径）
    const idx = productList.value.findIndex(p => p.id === targetId);
    if (idx !== -1) activeIndex.value = idx;

    if (currentV.paused) {
      // 显式同步 muted property，确保浏览器放行静音自动播放
      // （:muted 属性绑定首次渲染可能未同步到 DOM property，导致 play() 被自动播放策略拒绝）
      currentV.muted = isMuted.value;
      currentV.play().then(() => {
        // 播放成功，清除手动播放标记
        delete needManualPlay[targetId];
      }).catch(error => {
        if (error.name === 'NotAllowedError') {
          // 浏览器拦截了自动播放（移动端常见）：回退 playingId 让封面恢复，
          // 并标记该视频需用户手动点击播放
          needManualPlay[targetId] = true;
          if (playingId.value === targetId) playingId.value = null;
        } else {
          console.warn(`Play failed for ${targetId}:`, error.message);
        }
      });
    }
  } else if (retry < 5) {
    // ref 尚未绑定（首次打开时 DOM 可能还没渲染完），稍后重试
    setTimeout(() => playVideoById(targetId, retry + 1), 100);
  }
};

// 解锁自动播放：移动端/微信存在“首次交互锁”，页面加载后用户未交互时
// 连静音视频也会被拒绝自动播放。监听首次任意交互（触摸/点击/滚动），
// 立即播放当前视频，使用户无需专门去点中央按钮。
const unlockAutoplay = () => {
  const targetId = playingId.value
    || (productList.value[activeIndex.value] && productList.value[activeIndex.value].id);
  if (targetId) {
    const v = videoRefsMap[targetId];
    if (v && v.paused) {
      v.muted = isMuted.value;
      v.play().then(() => {
        delete needManualPlay[targetId];
        playingId.value = targetId;
      }).catch(() => {});
    }
  }
  removeUnlockListeners();
};

const removeUnlockListeners = () => {
  window.removeEventListener('touchstart', unlockAutoplay);
  window.removeEventListener('click', unlockAutoplay);
  window.removeEventListener('scroll', unlockAutoplay, true);
};

onMounted(() => {
  initWishlist();
  fetchProducts(true);
  // once: 触发一次即自动移除；capture 确保滚动也能捕获
  window.addEventListener('touchstart', unlockAutoplay, { once: true });
  window.addEventListener('click', unlockAutoplay, { once: true });
  window.addEventListener('scroll', unlockAutoplay, { once: true, capture: true });
});

onUnmounted(() => {
  removeUnlockListeners();
});
</script>

<style scoped>
/* 样式部分保持不变，仅保留关键部分以节省空间，实际使用时请保留完整样式 */
.video-page-container { width: 100vw; height: 100vh; background-color: #000; position: relative; overflow: hidden; color: #fff; font-family: sans-serif; }
.top-bar { position: absolute; top: 0; left: 0; width: 100%; height: 50px; z-index: 20; display: flex; align-items: center; padding: 0 10px; background: linear-gradient(to bottom, rgba(0,0,0,0.6), transparent); }
.menu-btn { width: 40px; height: 40px; display: flex; justify-content: center; align-items: center; cursor: pointer; z-index: 22; color: #fff; flex-shrink: 0; margin-right: 5px; }
.category-scroll-wrapper { flex: 1; display: flex; overflow-x: auto; white-space: nowrap; scrollbar-width: none; padding-left: 5px; }
.category-scroll-wrapper::-webkit-scrollbar { display: none; }
.category-item { display: inline-block; padding: 5px 12px; font-size: 15px; opacity: 0.8; transition: all 0.2s; cursor: pointer; color: #ddd; }
.category-item.active { opacity: 1; font-weight: bold; color: #fff; border-bottom: 2px solid #fff; }
.mute-btn { width: 40px; height: 40px; display: flex; justify-content: center; align-items: center; cursor: pointer; z-index: 21; color: #fff; flex-shrink: 0; margin-right: 8px; }
.video-scroll-container { width: 100%; height: 100%; overflow-y: scroll; scroll-snap-type: y mandatory; scroll-behavior: smooth; }
.video-item { width: 100%; height: 100%; scroll-snap-align: start; scroll-snap-stop: always; position: relative; display: flex; justify-content: center; align-items: center; }
.bg-video { width: 100%; height: 100%; object-fit: cover; }
.cover-overlay { transition: opacity 0.2s ease; position: absolute; top: 0; left: 0; width: 100%; height: 100%; z-index: 4; display: flex; justify-content: center; align-items: center; background: rgba(0, 0, 0, 0.3); pointer-events: auto; }
.cover-image { max-width: 90%; max-height: 90%; width: auto; height: auto; object-fit: contain; border-radius: 8px; box-shadow: 0 4px 12px rgba(0,0,0,0.5); }
.play-icon-overlay { position: absolute; top: 50%; left: 50%; transform: translate(-50%, -50%); pointer-events: none; filter: drop-shadow(0 2px 4px rgba(0,0,0,0.5)); }
.click-layer { position: absolute; top: 0; left: 0; width: 100%; height: 100%; z-index: 3; }
.video-status-overlay, .video-error-overlay { position: absolute; top: 0; left: 0; width: 100%; height: 100%; background-color: rgba(0,0,0,0.4); z-index: 5; display: flex; flex-direction: column; justify-content: center; align-items: center; }
.video-error-overlay { background-color: rgba(0,0,0,0.7); pointer-events: none; }
.manual-play-overlay { position: absolute; top: 0; left: 0; width: 100%; height: 100%; z-index: 6; display: flex; flex-direction: column; justify-content: center; align-items: center; background: rgba(0,0,0,0.35); }
.manual-play-btn { width: 76px; height: 76px; border-radius: 50%; background: rgba(0,0,0,0.55); border: 2px solid rgba(255,255,255,0.9); display: flex; justify-content: center; align-items: center; padding-left: 4px; animation: pulse-play 1.6s ease-in-out infinite; }
.manual-play-text { margin-top: 14px; color: #fff; font-size: 15px; text-shadow: 0 1px 3px rgba(0,0,0,0.8); letter-spacing: 1px; }
@keyframes pulse-play { 0% { transform: scale(1); box-shadow: 0 0 0 0 rgba(255,255,255,0.4); } 70% { transform: scale(1.05); box-shadow: 0 0 0 14px rgba(255,255,255,0); } 100% { transform: scale(1); box-shadow: 0 0 0 0 rgba(255,255,255,0); } }
.loading-spinner { width: 30px; height: 30px; border: 3px solid rgba(255,255,255,0.3); border-radius: 50%; border-top-color: #fff; animation: spin 1s ease-in-out infinite; margin-bottom: 10px; }
@keyframes spin { to { transform: rotate(360deg); } }
.status-text { font-size: 14px; color: #fff; }
.error-content { text-align: center; color: #ccc; pointer-events: auto; cursor: pointer; }
.error-icon { color: #ff4d4f; margin-bottom: 10px; }
.retry-hint { display: block; margin-top: 8px; font-size: 12px; color: rgba(255,255,255,0.6); }
.overlay { position: absolute; bottom: 0; left: 0; width: 100%; height: 60%; background: linear-gradient(to top, rgba(0,0,0,0.9), transparent); pointer-events: none; z-index: 1; }
.info-area { position: absolute; bottom: 95px; left: 15px; right: 80px; z-index: 5; text-align: left; }
.product-name { font-size: 18px; font-weight: bold; margin-bottom: 8px; text-shadow: 1px 1px 2px rgba(0,0,0,0.8); }
.info-row { font-size: 13px; margin-bottom: 3px; opacity: 0.9; text-shadow: 1px 1px 1px rgba(0,0,0,0.8); line-height: 1.4; }
.label { opacity: 0.7; margin-right: 5px; }
.right-actions { position: absolute; right: 10px; bottom: 95px; z-index: 5; display: flex; flex-direction: column; align-items: center; }
.action-btn { margin-bottom: 8px; cursor: pointer; transition: transform 0.2s; width: 40px; height: 40px; border-radius: 50%; background: rgba(255, 255, 255, 0.2); backdrop-filter: blur(4px); border: 1px solid rgba(255, 255, 255, 0.3); display: flex; justify-content: center; align-items: center; }
.action-btn:active { transform: scale(0.9); background: rgba(255, 255, 255, 0.4); }
.animate-like { animation: like-bounce 0.4s ease-in-out; }
@keyframes like-bounce { 0% { transform: scale(1); } 25% { transform: scale(1.3); } 50% { transform: scale(0.9); } 75% { transform: scale(1.1); } 100% { transform: scale(1); } }
.wishlist-btn svg { width: 22px; height: 22px; transition: fill 0.3s ease, stroke 0.3s ease; }
.speed-ball { width: 40px; height: 40px; border-radius: 50%; background: rgba(255, 255, 255, 0.2); backdrop-filter: blur(4px); border: 1px solid rgba(255, 255, 255, 0.3); display: flex; justify-content: center; align-items: center; font-size: 14px; font-weight: bold; cursor: pointer; }
.video-counter { position: absolute; right: 15px; bottom: 65px; z-index: 5; font-size: 14px; font-weight: 500; text-shadow: 1px 1px 2px rgba(0,0,0,0.8); }
.progress-control-area { position: absolute; bottom: 0; left: 0; width: 100%; height: 60px; z-index: 10; display: flex; flex-direction: column; justify-content: flex-end; padding-bottom: 15px; background: linear-gradient(to top, rgba(0,0,0,0.8), transparent); }
.progress-bar-container { position: relative; width: 100%; height: 20px; display: flex; align-items: center; cursor: pointer; padding: 0 5px; box-sizing: border-box; margin-bottom: 5px; }
.progress-bg { position: absolute; width: 100%; height: 2px; background: rgba(255,255,255,0.3); border-radius: 1px; }
.progress-current { position: absolute; height: 2px; background: #fff; border-radius: 1px; width: 0; }
.progress-thumb { position: absolute; width: 10px; height: 10px; background: #fff; border-radius: 50%; transform: translateX(-50%); opacity: 0; transition: opacity 0.2s; }
.progress-bar-container:hover .progress-thumb { opacity: 1; }
.time-display { position: absolute; right: 10px; bottom: 38px; font-size: 12px; color: #fff; text-shadow: 1px 1px 2px rgba(0,0,0,1); pointer-events: none; background: rgba(0,0,0,0.3); padding: 2px 6px; border-radius: 4px; }
.no-more-tip { position: absolute; bottom: 80px; width: 100%; text-align: center; font-size: 13px; color: rgba(255,255,255,0.4); z-index: 5; }
.bottom-spacer { height: 1px; }
.global-loading-mask { position: absolute; top: 0; left: 0; width: 100%; height: 100%; background: #000; z-index: 100; display: flex; flex-direction: column; justify-content: center; align-items: center; color: #fff; }
.drawer-mask { position: absolute; top: 0; left: 0; width: 100%; height: 100%; background: rgba(0,0,0,0.5); z-index: 40; }
.drawer-container { position: absolute; top: 0; left: 0; width: 280px; height: 100%; background: #1a1a1a; z-index: 41; box-shadow: 2px 0 10px rgba(0,0,0,0.5); display: flex; flex-direction: column; }
.drawer-header { height: 50px; display: flex; justify-content: space-between; align-items: center; padding: 0 15px; border-bottom: 1px solid #333; color: #fff; }
.close-drawer { font-size: 24px; cursor: pointer; }
.drawer-content { flex: 1; overflow-y: auto; padding: 10px 0; }
.empty-wishlist { text-align: center; color: #666; margin-top: 50px; }
.wishlist-item { display: flex; justify-content: space-between; align-items: center; padding: 10px 15px; border-bottom: 1px solid #333; cursor: pointer; transition: background 0.2s; }
.wishlist-item:hover { background: #2a2a2a; }
.item-info { flex: 1; overflow: hidden; }
.item-name { font-size: 14px; color: #fff; margin-bottom: 4px; white-space: nowrap; overflow: hidden; text-overflow: ellipsis; }
.item-spec { font-size: 12px; color: #999; }
.item-delete { padding: 5px; color: #666; cursor: pointer; }
.item-delete:hover { color: #ff4d4f; }
.drawer-footer { padding: 8px; border-top: 1px solid #333; background: #1a1a1a; width: 100%; box-sizing: border-box; }
.input-group { display: flex; gap: 8px; width: 100%; align-items: center; }
.phone-input { flex: 1; padding: 8px 10px; border-radius: 4px; border: 1px solid #444; background: #2a2a2a; color: #fff; font-size: 14px; outline: none; min-width: 0; box-sizing: border-box; }
.phone-input:focus { border-color: #ff4d4f; }
.action-buttons { display: flex; gap: 8px; flex-shrink: 0; }
.submit-btn { padding: 8px 12px; border-radius: 4px; border: none; background: #ff4d4f; color: #fff; font-size: 14px; cursor: pointer; transition: background 0.2s; white-space: nowrap; min-width: 60px; }
.submit-btn:hover:not(:disabled) { background: #ff7875; }
.submit-btn:disabled { background: #555; cursor: not-allowed; opacity: 0.7; }
.clear-btn { padding: 8px 12px; border-radius: 4px; border: 1px solid #555; background: transparent; color: #ccc; font-size: 14px; cursor: pointer; transition: all 0.2s; white-space: nowrap; min-width: 60px; }
.clear-btn:hover:not(:disabled) { border-color: #888; color: #fff; background: rgba(255,255,255,0.05); }
.clear-btn:disabled { opacity: 0.5; cursor: not-allowed; }
.toast-container { position: fixed; top: 20%; left: 50%; transform: translateX(-50%); background-color: rgba(0, 0, 0, 0.85); color: #fff; padding: 12px 24px; border-radius: 8px; font-size: 14px; z-index: 1000; box-shadow: 0 4px 12px rgba(0,0,0,0.3); backdrop-filter: blur(4px); max-width: 80%; text-align: center; pointer-events: none; }
.toast-container.error { background-color: rgba(45, 45, 45, 0.95); border: 1px solid #ff4d4f; color: #ffccc7; }
.toast-fade-enter-active, .toast-fade-leave-active { transition: opacity 0.3s ease, transform 0.3s ease; }
.toast-fade-enter-from, .toast-fade-leave-to { opacity: 0; transform: translateX(-50%) translateY(-20px); }
.toast-fade-enter-to, .toast-fade-leave-from { opacity: 1; transform: translateX(-50%) translateY(0); }
.drawer-slide-enter-active, .drawer-slide-leave-active { transition: all 0.3s ease; }
.drawer-slide-enter-from, .drawer-slide-leave-to { transform: translateX(-100%); opacity: 0; }
.drawer-slide-enter-to, .drawer-slide-leave-from { transform: translateX(0); opacity: 1; }
</style>